import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { formatApiError } from "@/lib/api";
import { auth } from "@/api";
import {
  deactivateExistingPushSubscription,
  syncExistingPushSubscription,
} from "@/lib/pushNotifications";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = logged in
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const data = await auth.me();
      setUser(data);

      // A permission already granted must never require another manual click.
      syncExistingPushSubscription().catch(() => {});

      return data;
    } catch (e) {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    setError("");
    try {
      await auth.login(email, password);
      return await refresh();
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  };

  const register = async (payload) => {
    setError("");
    try {
      await auth.register(payload);
      return await refresh();
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try {
      await deactivateExistingPushSubscription();
    } catch {}

    try {
      await auth.logout();
    } catch {}

    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, error, login, register, logout, refresh, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() { return useContext(AuthCtx); }
